import React from "react";
import EmailIcon from "@material-ui/icons/Email";
import BusinessIcon from "@material-ui/icons/Business";
import LanguageIcon from "@material-ui/icons/Language";
import SecurityIcon from "@material-ui/icons/Security";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import AssignmentIcon from "@material-ui/icons/Assignment";
import PeopleIcon from "@material-ui/icons/People";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import VideoCallIcon from "@material-ui/icons/VideoCall";
import DescriptionIcon from "@material-ui/icons/Description";
import HealingIcon from "@material-ui/icons/Healing";
import ScheduleIcon from "@material-ui/icons/Schedule";
import MessageIcon from "@material-ui/icons/Message";
import PhoneIcon from "@material-ui/icons/Phone";

const footerStyles = {
  footer: {
    background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
    color: 'white',
    padding: '60px 20px 30px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  
  footerContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  
  footerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '40px',
    marginBottom: '40px',
  },
  
  footerSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  footerTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#fff',
  },
  
  footerLink: {
    color: '#cbd5e0',
    textDecoration: 'none',
    marginBottom: '12px',
    fontSize: '0.95rem',
    transition: 'color 0.3s ease',
    cursor: 'pointer',
  },
  
  footerText: {
    color: '#a0aec0',
    lineHeight: '1.6',
    marginBottom: '12px',
    fontSize: '0.95rem',
  },
  
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  
  logo: {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    marginBottom: '15px',
    objectFit: 'cover',
  },
  
  companyName: {
    fontSize: '1.5rem',
    fontWeight: '700',
    marginBottom: '15px',
    color: '#fff',
  },
  
  description: {
    color: '#a0aec0',
    lineHeight: '1.6',
    fontSize: '0.9rem',
  },
  
  socialLinks: {
    display: 'flex',
    gap: '15px',
    marginTop: '20px',
  },
  
  socialLink: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#cbd5e0',
    textDecoration: 'none',
    fontSize: '1.2rem',
    transition: 'all 0.3s ease',
  },
  
  bottomBar: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px',
  },
  
  copyright: {
    color: '#a0aec0',
    fontSize: '0.9rem',
  },
  
  backToTop: {
    color: '#4facfe',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '600',
    transition: 'color 0.3s ease',
  },
  
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
  },
  
  contactIcon: {
    marginRight: '10px',
    fontSize: '1rem',
  },
};

const Footer = () => {
  const Logo = process.env.REACT_APP_TENANT_LOGO;
  const TenantName = process.env.REACT_APP_TENANT_NAME || "Telehealth Platform";
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer style={footerStyles.footer}>
      <style>
        {`
          .footer-link:hover {
            color: #4facfe !important;
          }
          
          .social-link:hover {
            background: rgba(79, 172, 254, 0.2) !important;
            color: #4facfe !important;
            transform: translateY(-2px);
          }
          
          .back-to-top:hover {
            color: #fff !important;
          }
        `}
      </style>
      
      <div style={footerStyles.footerContainer}>
        <div style={footerStyles.footerGrid}>
          {/* Company Info Section */}
          <div style={footerStyles.logoSection}>
            <img src={Logo} alt={`${TenantName} Logo`} style={footerStyles.logo} />
            <h3 style={footerStyles.companyName}>{TenantName}</h3>
            <p style={footerStyles.description}>
              Connecting patients with healthcare professionals through secure, convenient telemedicine. 
              Access quality healthcare from the comfort of your home.
            </p>
            <div style={footerStyles.socialLinks}>
              <a href="mailto:admin@hcgconsultinggroup.com" style={footerStyles.socialLink} className="social-link">
                <EmailIcon />
              </a>
              <a href="#" style={footerStyles.socialLink} className="social-link">
                <PhoneIcon />
              </a>
              <a href="#" style={footerStyles.socialLink} className="social-link">
                <LanguageIcon />
              </a>
              <a href="#" style={footerStyles.socialLink} className="social-link">
                <BusinessIcon />
              </a>
            </div>
          </div>

          {/* Quick Links Section */}
          <div style={footerStyles.footerSection}>
            <h4 style={footerStyles.footerTitle}>Quick Links</h4>
            <a href="/patient/login" style={{...footerStyles.footerLink, display: 'flex', alignItems: 'center'}} className="footer-link">
              <PeopleIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Patient Portal
            </a>
            <a href="/admin/login" style={{...footerStyles.footerLink, display: 'flex', alignItems: 'center'}} className="footer-link">
              <LocalHospitalIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Provider Portal
            </a>
            <a href="/admin/provider/registration" style={{...footerStyles.footerLink, display: 'flex', alignItems: 'center'}} className="footer-link">
              <AssignmentIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Provider Registration
            </a>
            <a href="/users/login" style={{...footerStyles.footerLink, display: 'flex', alignItems: 'center'}} className="footer-link">
              <SupervisorAccountIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Administration
            </a>
          </div>

          {/* Services Section */}
          <div style={footerStyles.footerSection}>
            <h4 style={footerStyles.footerTitle}>Services</h4>
            <span style={{...footerStyles.footerText, display: 'flex', alignItems: 'center'}}>
              <VideoCallIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Virtual Consultations
            </span>
            <span style={{...footerStyles.footerText, display: 'flex', alignItems: 'center'}}>
              <DescriptionIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Electronic Health Records
            </span>
            <span style={{...footerStyles.footerText, display: 'flex', alignItems: 'center'}}>
              <HealingIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Prescription Management
            </span>
            <span style={{...footerStyles.footerText, display: 'flex', alignItems: 'center'}}>
              <ScheduleIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Appointment Scheduling
            </span>
            <span style={{...footerStyles.footerText, display: 'flex', alignItems: 'center'}}>
              <MessageIcon style={{marginRight: '8px', fontSize: '1rem'}} /> Secure Messaging
            </span>
          </div>

          {/* Contact Info Section */}
          <div style={footerStyles.footerSection}>
            <h4 style={footerStyles.footerTitle}>Contact Us</h4>
            <div style={footerStyles.contactItem}>
              <EmailIcon style={{...footerStyles.contactIcon, fontSize: '1rem'}} />
              <a 
                href="mailto:admin@hcgconsultinggroup.com" 
                style={footerStyles.footerLink} 
                className="footer-link"
              >
                admin@hcgconsultinggroup.com
              </a>
            </div>
            <div style={footerStyles.contactItem}>
              <BusinessIcon style={{...footerStyles.contactIcon, fontSize: '1rem'}} />
              <span style={footerStyles.footerText}>HCG Consulting Group</span>
            </div>
            <div style={footerStyles.contactItem}>
              <LanguageIcon style={{...footerStyles.contactIcon, fontSize: '1rem'}} />
              <span style={footerStyles.footerText}>Available 24/7</span>
            </div>
            <div style={footerStyles.contactItem}>
              <SecurityIcon style={{...footerStyles.contactIcon, fontSize: '1rem'}} />
              <span style={footerStyles.footerText}>HIPAA Compliant</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={footerStyles.bottomBar}>
          <div style={footerStyles.copyright}>
            &copy; {new Date().getFullYear()} HCG Consulting Group. All rights reserved. |{" "}
            <a href="#" style={footerStyles.footerLink} className="footer-link">Privacy Policy</a> |{" "}
            <a href="#" style={footerStyles.footerLink} className="footer-link">Terms of Service</a> |{" "}
            <a href="#" style={footerStyles.footerLink} className="footer-link">HIPAA Notice</a>
          </div>
          <button 
            onClick={scrollToTop}
            style={{...footerStyles.backToTop, background: 'none', border: 'none', cursor: 'pointer'}}
            className="back-to-top"
          >
            ↑ Back to Top
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
